import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Select, Typography } from 'antd';

import {
	BotPostSendTimeEnum,
	useAccountSettingsPartialUpdateMutation,
	useAccountSettingsRetrieveQuery,
} from 'entities/User';
import { i18n } from 'i18n';
import { useCommonSlice } from 'shared/hooks';
import { SwitchWithText } from 'shared/ui';
import { WebApp } from 'shared/utils';

import styles from './SettingsNotifications.module.scss';

const sendTimeOption = [
	{ label: i18n.t('За 10 минут'), value: BotPostSendTimeEnum.$10 },
	{ label: i18n.t('За полчаса'), value: BotPostSendTimeEnum.$30 },
	{ label: i18n.t('За час'), value: BotPostSendTimeEnum.$60 },
	{ label: i18n.t('Никогда'), value: BotPostSendTimeEnum.Never },
];

interface ISettingsNotificationsProps {}

export const SettingsNotifications: FC<ISettingsNotificationsProps> = () => {
	const { t } = useTranslation();

	const { isAllowWrite, setIsAllowWrite } = useCommonSlice();

	const { data: settings, isFetching: isSettingsFetching } = useAccountSettingsRetrieveQuery();
	const [accountSettingsPartialUpdate, { isLoading: isUpdateLoading }] = useAccountSettingsPartialUpdateMutation();

	const isLoading = isSettingsFetching || isUpdateLoading;

	const sendAfterPublicationIsEnabled =
		!!settings?.bot_post_send_time && settings?.bot_post_send_time !== BotPostSendTimeEnum.Never;
	const notifyOnTaskCompletionIsEnabled = !!settings?.bot_notify_on_task_completion;
	const sendDigestIsEnabled = !!settings?.bot_allow_send_digest;

	return (
		<Flex vertical gap={8} className={styles.content}>
			<Typography.Title level={5} style={{ margin: 0 }}>
				{t('Уведомления')}
			</Typography.Title>
			<div className={styles.section}>
				<SwitchWithText
					subTitle={t(
						'Разрешите боту присылать вам системные уведомления, без рекламы и спама. Бот не может удалять или публиковать посты без вашего согласия.',
					)}
					onChange={() => WebApp?.requestWriteAccess((accessGranted) => setIsAllowWrite(accessGranted))}
					checked={isAllowWrite}>
					{t('Разрешить получать системные уведомления от бота')}
				</SwitchWithText>
			</div>

			<div className={styles.section}>
				<SwitchWithText
					subTitle={t(
						'Вы можете гибко настроить, какие уведомления из перечисленных ниже хотите получать. Бот не может удалять или публиковать посты без вашего согласия.',
					)}
					onChange={(value) => {
						const updateSettings = () => {
							accountSettingsPartialUpdate(
								value
									? {
											bot_post_send_time: BotPostSendTimeEnum.$10,
											bot_notify_on_task_completion: true,
											bot_allow_send_digest: true,
										}
									: {
											bot_post_send_time: BotPostSendTimeEnum.Never,
											bot_notify_on_task_completion: false,
											bot_allow_send_digest: false,
										},
							);
						};

						if (isAllowWrite || !value) {
							updateSettings();
						} else {
							WebApp?.requestWriteAccess((accessGranted) => {
								setIsAllowWrite(accessGranted);
								accessGranted && updateSettings();
							});
						}
					}}
					checked={
						(sendAfterPublicationIsEnabled || notifyOnTaskCompletionIsEnabled || sendDigestIsEnabled) && isAllowWrite
					}
					loading={isLoading}
					disabled={isLoading}>
					{t('Разрешить получать необязательные уведомления от бота')}
				</SwitchWithText>
			</div>

			<Flex vertical gap={8} className={styles.section}>
				<SwitchWithText
					onChange={(value) => {
						const updateSettings = () => {
							accountSettingsPartialUpdate(
								value
									? { bot_post_send_time: BotPostSendTimeEnum.$10 }
									: { bot_post_send_time: BotPostSendTimeEnum.Never },
							);
						};

						if (isAllowWrite || !value) {
							updateSettings();
						} else {
							WebApp?.requestWriteAccess((accessGranted) => {
								setIsAllowWrite(accessGranted);
								accessGranted && updateSettings();
							});
						}
					}}
					checked={sendAfterPublicationIsEnabled && isAllowWrite}
					loading={isLoading}
					disabled={isLoading}>
					{t('Присылать пост в бота перед публикацией')}
				</SwitchWithText>

				<Select
					style={{ width: '100%' }}
					value={isAllowWrite ? settings?.bot_post_send_time : BotPostSendTimeEnum.Never}
					options={sendTimeOption}
					onChange={(value) => {
						const updateSettings = () => {
							accountSettingsPartialUpdate({ bot_post_send_time: value });
						};

						if (isAllowWrite || value === BotPostSendTimeEnum.Never) {
							updateSettings();
						} else {
							WebApp?.requestWriteAccess((accessGranted) => {
								setIsAllowWrite(accessGranted);
								accessGranted && updateSettings();
							});
						}
					}}
					size="large"
					loading={isLoading}
					disabled={isLoading}
				/>
			</Flex>

			<div className={styles.section}>
				<SwitchWithText
					onChange={(value) => {
						const updateSettings = () => {
							accountSettingsPartialUpdate({ bot_notify_on_task_completion: value });
						};

						if (isAllowWrite || !value) {
							updateSettings();
						} else {
							WebApp?.requestWriteAccess((accessGranted) => {
								setIsAllowWrite(accessGranted);
								accessGranted && updateSettings();
							});
						}
					}}
					checked={notifyOnTaskCompletionIsEnabled && isAllowWrite}
					loading={isLoading}
					disabled={isLoading}>
					{t('Уведомлять о выполнении заданий')}
				</SwitchWithText>
			</div>

			<div className={styles.section}>
				<SwitchWithText
					onChange={(value) => {
						const updateSettings = () => {
							accountSettingsPartialUpdate({ bot_allow_send_digest: value });
						};

						if (isAllowWrite || !value) {
							updateSettings();
						} else {
							WebApp?.requestWriteAccess((accessGranted) => {
								setIsAllowWrite(accessGranted);
								accessGranted && updateSettings();
							});
						}
					}}
					checked={sendDigestIsEnabled && isAllowWrite}
					loading={isLoading}
					disabled={isLoading}>
					{t('Присылать статистику за прошедшие 7 дней')}
				</SwitchWithText>
			</div>
		</Flex>
	);
};
